import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Lock, Loader2, ShieldCheck, ArrowRight, Building2, Globe2, AlertTriangle, CheckCircle2,
} from "lucide-react";

const UNLOCK_KEY = "bnp_report_unlock";
const VARIANT_KEY = "bnp_gate_headline_variant";

/**
 * Two headline variants for a lightweight A/B test. The assignment is sticky
 * per browser (localStorage) and reported to GA4 via the `variant` dimension on
 * the `gate_view` and `report_unlocked` events, so we can compare unlock rates.
 *   A = control (longer, descriptive)   B = short & punchy
 */
function getHeadlineVariant() {
  try {
    let v = localStorage.getItem(VARIANT_KEY);
    if (v !== "a" && v !== "b") {
      v = Math.random() < 0.5 ? "a" : "b";
      localStorage.setItem(VARIANT_KEY, v);
    }
    return v;
  } catch (_) {
    return "a";
  }
}

function headlineCopy(variant, query) {
  if (variant === "b") {
    return {
      title: <>Unlock your report</>,
      sub: "Add your details and we'll show your full risk report instantly, plus email you a copy.",
      formHeading: "One quick step",
      button: "Show my report",
    };
  }
  return {
    title: <>Your risk report for <span className="text-[#71edc9]">&ldquo;{query}&rdquo;</span> is ready</>,
    sub: "Enter your details to unlock it. We'll reveal your full report on screen and email you a copy for safekeeping.",
    formHeading: "Unlock your full report",
    button: "Unlock my report",
  };
}

// Light-touch UK phone helpers: sanitise input to sensible characters and
// validate the digit count (UK numbers are 10-11 digits, or +44 form).
function sanitisePhone(v) {
  return v.replace(/[^\d+()\-\s]/g, "").slice(0, 20);
}
function phoneDigits(v) {
  return (v.match(/\d/g) || []).length;
}
function isValidUkPhone(v) {
  const d = phoneDigits(v);
  return d >= 10 && d <= 13;
}
function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
}

/**
 * Lead-capture gate shown on /check BEFORE the risk report is revealed.
 * Visitor must enter name + email + phone. On submit we call
 * POST /api/name-check/unlock which runs the check, emails a PDF copy and
 * schedules the drip, then returns the report so it can be shown on screen.
 * Details are persisted to sessionStorage so we only ask once per session.
 */
export default function ReportUnlockGate({ query, industry = "", postcode = "", onUnlocked }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const partialSentRef = useRef("");

  // Capture the email as a partial lead the moment a valid one is entered,
  // BEFORE the full unlock - so no lead is lost if the visitor bails. Tagged
  // source='check_partial_email' server-side; upgraded in place on full unlock.
  const capturePartial = () => {
    const em = form.email.trim().toLowerCase();
    if (!em || !isValidEmail(em) || partialSentRef.current === em) return;
    if (!query || query.length < 2) return;
    partialSentRef.current = em;
    api.post("/name-check/partial-lead", {
      email: em,
      query,
      industry: industry || null,
      postcode: postcode || null,
    }).catch(() => { /* best-effort; never blocks the visitor */ });
  };

  const variant = useMemo(() => getHeadlineVariant(), []);
  const copy = headlineCopy(variant, query);

  // GA4 `gate_view` - fires once per mount, carries the A/B variant so the
  // unlock rate can be attributed per variant.
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "gate_view", { variant, search_term: query });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) return;
    if (!isValidUkPhone(form.phone)) {
      setPhoneError("Please enter a valid UK phone number.");
      return;
    }
    setPhoneError("");
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/name-check/unlock", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        query,
        industry: industry || null,
        postcode: postcode || null,
      });
      try {
        sessionStorage.setItem(
          UNLOCK_KEY,
          JSON.stringify({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() })
        );
      } catch (_) { /* private mode: session persistence optional */ }
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "report_unlocked", { search_term: query, variant });
        window.gtag("event", "generate_lead", { search_term: query, method: "report_unlock", variant });
      }
      onUnlocked(r.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "We couldn't unlock your report. Please try again.");
      setBusy(false);
    }
  };

  const teaser = [
    { icon: Building2, label: "Companies House similarity", sub: "How close your name is to existing UK companies" },
    { icon: Globe2, label: "Domain availability", sub: "Across the major UK web extensions" },
    { icon: AlertTriangle, label: "Your risk score & recommendations", sub: "A clear 0-100 score and what to do next" },
  ];

  return (
    <div className="bnp-card overflow-hidden bnp-fade-up" data-testid="report-unlock-gate" data-variant={variant}>
      <div className="grid lg:grid-cols-5">
        {/* Left: teaser of what's locked */}
        <div className="lg:col-span-2 bg-[#04273d] text-white p-7 md:p-9 relative">
          <span className="inline-flex items-center gap-1.5 font-mono-brand text-[10px] tracking-widest uppercase bg-white/10 text-[#71edc9] px-3 py-1.5 rounded-full">
            <Lock size={12} /> Report ready
          </span>
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-4 leading-tight" data-testid="report-unlock-headline">
            {copy.title}
          </h3>
          <p className="text-[#9ac7f7] text-sm mt-3 leading-relaxed">
            {copy.sub}
          </p>

          <ul className="mt-6 space-y-3.5">
            {teaser.map(({ icon: Icon, label, sub }) => (
              <li key={label} className="flex items-start gap-3">
                <span className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-[#71edc9]" />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-sm font-semibold leading-tight">{label}</p>
                  <p className="text-[12px] text-[#9ac7f7] leading-snug mt-0.5">{sub}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex items-center gap-2 text-[12px] text-[#9ac7f7]">
            <ShieldCheck size={14} className="text-[#71edc9] shrink-0" /> Takes 30 seconds. No obligation. UK-based team.
          </div>
        </div>

        {/* Right: the form */}
        <div className="lg:col-span-3 p-7 md:p-9">
          <h4 className="font-display text-xl md:text-2xl text-[#04273d] font-bold tracking-tight">
            {copy.formHeading}
          </h4>
          <p className="text-sm text-[#447694] mt-1.5">
            Just three quick details and your report is yours.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-3.5" data-testid="report-unlock-form">
            <div>
              <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Your name *</label>
              <input
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Sophie Bell"
                className="bnp-input"
                data-testid="report-unlock-name"
              />
            </div>
            <div>
              <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Your email *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                onBlur={capturePartial}
                placeholder="you@yourbusiness.co.uk"
                className="bnp-input"
                data-testid="report-unlock-email"
              />
            </div>
            <div>
              <label className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase mb-1 block">Your phone number *</label>
              <input
                type="tel"
                required
                inputMode="tel"
                value={form.phone}
                onChange={(e) => { set("phone", sanitisePhone(e.target.value)); if (phoneError) setPhoneError(""); }}
                placeholder="07123 456789"
                className={`bnp-input ${phoneError ? "border-red-400 focus:border-red-400" : ""}`}
                data-testid="report-unlock-phone"
              />
              {phoneError && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="report-unlock-phone-error">{phoneError}</p>
              )}
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="report-unlock-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center text-base py-3.5 bnp-pulse"
              data-testid="report-unlock-submit"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              {busy ? "Unlocking your report…" : copy.button}
              {!busy && <ArrowRight size={16} />}
            </button>

            <p className="text-[11px] text-[#447694] leading-relaxed flex items-start gap-1.5" data-testid="report-unlock-consent">
              <CheckCircle2 size={13} className="text-[#71edc9] mt-0.5 shrink-0" />
              <span>
                By submitting, you agree to receive your report and occasional business name protection updates from NBR Group.
                We never sell your details on, and you can unsubscribe with one click.
              </span>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export { UNLOCK_KEY };
